import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { Button } from '../../cdl/Button/Button';
import { queries } from '../../common/api/queryKeys/queries';
import { ButtonGroup } from '../../common/buttons/ButtonGroup';
import { translate } from '../../common/helpers/translate.helper';
import { OfferModel } from '../model/OfferModel';
import { OfferState } from '../model/OfferState';

import { AcknowledgePopup } from './common/components/AcknowledgePopup';
import { StateSwitchContextProvider } from './common/components/StateSwitchContextProvider';
import { useSwitchState } from './common/hooks/useSwitchState';
import { SupplierConfirmButton } from './components/SupplierConfirmButton';
import { ConfirmDeliveryPopup } from './ConfirmDeliveryPopup';

interface LubesSupplierOfferDetailHeaderActionProps {
    offer: OfferModel;
}

const LubesSupplierOfferDetailHeaderActionContent = ({ offer }: LubesSupplierOfferDetailHeaderActionProps) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const { changeState } = useSwitchState();

    const navigateToQuote = () => {
        navigate({ to: `/offer/$id/quote`, params: { id: offer.id }, state: { originLocation: location } });
        queryClient.removeQueries({ queryKey: queries.offers.detail(offer.id).queryKey });
    };

    switch (offer.state) {
        case OfferState.ENQUIRY:
            return (
                <Button emphasis="high" onClick={navigateToQuote}>
                    {translate(
                        offer.isSpot() ? 'statusbar.supplier.spotenquiry.button' : 'statusbar.supplier.enquiry.button'
                    )}
                </Button>
            );
        case OfferState.CONFIRMED:
            if (!offer.deliveryPast()) {
                return null;
            }

            return (
                <Button
                    emphasis="high"
                    onClick={() => {
                        changeState({
                            offer,
                            destinationState: OfferState.DELIVERED,
                            component: ConfirmDeliveryPopup,
                        });
                    }}
                >
                    {translate('statusbar.supplier.delivery.button')}
                </Button>
            );
        case OfferState.ORDER:
            return (
                <ButtonGroup>
                    <Button
                        onClick={() => {
                            changeState({
                                offer,
                                destinationState: OfferState.ACKNOWLEDGED,
                                component: AcknowledgePopup,
                            });
                        }}
                    >
                        {translate('statusbar.supplier.order.acknowledgeButton')}
                    </Button>

                    <SupplierConfirmButton offer={offer} />
                </ButtonGroup>
            );
        case OfferState.ACKNOWLEDGED:
            return <SupplierConfirmButton offer={offer} />;
        default:
            return null;
    }
};

export const LubesSupplierOfferDetailHeaderAction = (props: LubesSupplierOfferDetailHeaderActionProps) => {
    return (
        <StateSwitchContextProvider>
            <LubesSupplierOfferDetailHeaderActionContent {...props} />
        </StateSwitchContextProvider>
    );
};
